import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnInit,
    Optional,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';

import {BaseComponent} from '../base.component';
import {AppToastrService} from '../../services/toastr.service';
import {FileUploadService} from './file-upload.service';
import {HttpErrorResponse} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {filter, map, switchMap} from 'rxjs/operators';
import {
    GET_ATTACHMENT_UPLOAD_URL,
    IDownloadUrlResponse,
    IFileInfo,
    PhotoData,
    RecommendedDimensions,
    UPLOAD_URL,
    UPLOAD_VISITOR_URL
} from '@ui-app/models/common.models';
import {ImageService} from '@ui-app/services/image.service';
import {CommonConfig} from '@ui-app/configs/common.config';
import {AutomaticCroppingService} from '@ui-app/services/automatic-cropping.service';
import {EReturnImageResizeType} from '@shared-models/common.enums';
import {S3DownloadUrlService} from '@ui-app/services/s3-download-url.service';
import {AttachmentService} from '../../../../../DBadgeEmployee-app/src/app/core/services/data-access/attachment.service';
import {BadgeRequestCategoryEnum, IParserResponse} from '@ui-app/models/common.models';
import { BulkVisitorUploadService } from 'DBadgeEmployee-app/src/app/core/services/data-access/bulk-visitor-upload.service';
import { BulkCleUploadService } from 'DBadgeEmployee-app/src/app/core/services/data-access/bulk-cle-upload.service';

@Component({
    selector: 'ui-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.less'],
    providers: [ImageService]
})
export class FileUploadComponent extends BaseComponent implements OnInit, OnChanges {

    @Input() areaTitle: string = 'Drag & Drop file here or';
    @Input() newFileName: string = '';
    @Input() customRestrictions: string = '';
    @Input() uploadedCount: number = -1;
    @Input() maxFilesCount: number = 1;
    @Input() resolvedExtensions: string[] = [];
    @Input() maxSizePerFile: number = 0;
    @Input() recommendedDimensions = {} as RecommendedDimensions;
    @Input() errors: Record<string, string> = {};
    @Input() isRequiredWarning: boolean;
    @Input() isUploadingWarning: boolean;
    @Input() isVisitorRequest = false;
    @Input() isAttachment = false;
    @Input() isVisitorBulkUpload = false;
    @Input() isCleBulkUpload = false;
    @Input() isExtension = false;
    @Input() requestCategoryId: BadgeRequestCategoryEnum;

    @Output() uploaded = new EventEmitter<PhotoData[]>();
    @Output() bulkVisitorFileUploaded = new EventEmitter<IParserResponse<unknown>>();
    @ViewChild('fileInput', { static: false }) fileInput: ElementRef;

    selectedFilesList: File[] = [];
    acceptedExtensions: string[] = [];
    maxSizePerFileConverted: number;
    uploadInProgress: boolean = false;
    private availableCount: number;
    isDragEvent: boolean;
    uploadedFiles: IFileInfo[] = [];
    imageDefaultType: string = CommonConfig.cameraConfig.defaultImageType;
    minHeight = CommonConfig.cameraConfig.height;
    minWidth = CommonConfig.cameraConfig.width;
    isImageRecognitionEnabled: boolean;
    url: string;
    fileSize: number;

    get deleteIcon(): string {
        return this.isAttachment ? 'icon-delete' : 'icon-cross_16px';
    }

    constructor(
        private toastrSevice: AppToastrService,
        private imageService: ImageService,
        private attachmentService: AttachmentService,
        private downloadUrlService: S3DownloadUrlService,
        private visitorUploadService: BulkVisitorUploadService,
        private cleUploadService: BulkCleUploadService,
        @Optional() @Inject(FileUploadService) private fileUploadService: FileUploadService,
        private automaticCroppingService: AutomaticCroppingService,
        @Optional() @Inject(UPLOAD_VISITOR_URL) private visitorUrl: string,
        @Optional() @Inject(UPLOAD_URL) private employeeUrl: string,
        @Optional() @Inject(GET_ATTACHMENT_UPLOAD_URL) private attachmentUrl: string,
    ) {
        super();
    }

    ngOnInit(): void {
        this.url = this.isVisitorRequest ? this.visitorUrl : this.employeeUrl;
        if (!this.url) {
            throw new Error('UPLOAD_URL not provided');
        }
        if (!this.fileUploadService) {
            throw new Error('FileUploadService not provided');
        }
        this.acceptedExtensions = this.resolvedExtensions.map((extension: string) => `.${extension}`.toLowerCase());
        this.maxSizePerFileConverted = Math.floor(this.maxSizePerFile / 1024 / 1024);
        this.subs = this.automaticCroppingService.getSetting().subscribe((settings) => {
            this.isImageRecognitionEnabled = settings.isImageRecognitionEnabled;
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.availableCount = (this.uploadedCount > -1)
            ? this.maxFilesCount - this.uploadedCount
            : this.maxFilesCount;
    }

    handleFiles(files: FileList): void {
        const uploadedFilesList: File[] = Array.prototype.slice.call(files, 0);

        this.subs = this.validateFiles(uploadedFilesList)
            .subscribe((validFiles) => {
                this.selectedFilesList.push(...validFiles);
                if (validFiles.length) {
                    validFiles.forEach((validFile: File) => {
                        this.uploadFile(validFile);
                    });
                }
                // Clear input value to be able to select the same file
                this.fileInput.nativeElement.value = null;
            });
    }

    private validateFiles(filesList: Array<File>): Observable<File[]> {
        const validFiles: File[] = [];
        let errorMessage = '';

        if (this.availableCount && filesList.length > this.availableCount) {
            errorMessage = this.errors.filesCount ||
                `Only ${this.maxFilesCount} file${this.maxFilesCount > 1 ? 's' : ''}
                ${this.maxFilesCount > 1 ? 'are' : 'is'} allowed to upload`;

            this.toastrSevice.error(errorMessage, null);
            return of(validFiles);
        }

        // Check file dimensions with onload in additional function
        const getPhotoDimensions = this.checkPhotoDimensions(filesList)
            .pipe(map((dimensions) => {
                if (dimensions.height < this.minHeight || dimensions.width < this.minWidth) {
                    errorMessage = this.errors.dimensions ||
                        `Photo is small. Min size is ${this.minWidth}*${this.minHeight} pixels`;
                    this.toastrSevice.error(errorMessage, null);
                    return [];
                } else {
                    return filesList;
                }
            }));

        // Standard file validation
        const fileValidation = (files: File[]) => {
            return of(files.filter((file: File) => {
                this.fileSize = file.size;
                const fileExtension: string = '.' + file.name.split('.').pop().toLowerCase();
                const isValidExtension: boolean = this.acceptedExtensions.includes(fileExtension);
                const isFileMaxSizeExceeded: boolean = this.maxSizePerFile && file.size > this.maxSizePerFile;
                if (!isValidExtension) {
                    errorMessage = this.errors.extensions ||
                        `File format is incorrect. Please choose the file in one of supported format`;
                } else if (isValidExtension && isFileMaxSizeExceeded) {
                    errorMessage = this.errors.fileSize ||
                        `File size is exceeded. Max size is ${this.maxSizePerFileConverted} Mb`;
                } else {
                    return true;
                }
                this.toastrSevice.error(errorMessage, null);
                return false;
            }));
        };

        // Combining all the file validations in one

        if (!Object(this.recommendedDimensions).keys) {
            return fileValidation(filesList).pipe(
                filter(files => files.length > 0),
            );
        } else {
            return getPhotoDimensions.pipe(
                switchMap((files) => fileValidation(files)),
                filter(files => files.length > 0),
            );
        }
    }

    uploadFileFirstStep(file: File): Observable<any> {
        if (this.isAttachment) {
            this.url = this.attachmentUrl;
        }

        if (!this.isImageRecognitionEnabled) {
            return this.imageService
                .changeImageSize(file, this.imageDefaultType.split('/')[1], 600, EReturnImageResizeType.file, this.newFileName)
                .pipe(
                    switchMap(resized => {
                        return this.fileUploadService.uploadFile(resized, this.url);
                    }),
                );
        }
        const renamedFile = this.imageService.changeFileName(file, this.newFileName);
        return this.fileUploadService.uploadFile(renamedFile, this.url);
    }

    private uploadFile(file: File): void {
        this.uploadInProgress = true;
        let photoData: PhotoData;
        let request;
        if (this.isVisitorBulkUpload) {
            request = this.uploadVisitorFile(file);
        } 
        else if (this.isCleBulkUpload) {
            request = this.uploadCleFile(file);
        } else {
            request = this.uploadFileFirstStep(file)
                .pipe(
                    switchMap((photo: PhotoData) => {
                        photoData = photo;
                        if (this.isAttachment) {
                            return this.attachmentService.getAttachmentDownloadUrl(photo.uId);
                        } else {
                            return this.downloadUrlService.getUrl(photo.uId, this.isVisitorRequest);
                        }
                    }),
                    map((downloadUrl: IDownloadUrlResponse) => ({
                        ...photoData,
                        ...downloadUrl
                    }))
                )
        }
        this.subs = request.subscribe(data => {
                this.uploadInProgress = false;
                this.uploadedFiles.push({
                    name: file.name,
                    type: file.type,
                    size: data.length,
                    ...data
                });
                if (this.isVisitorBulkUpload) {
                    this.handleUploadValidation(data);
                }
                else if(this.isCleBulkUpload){
                    this.handleCleUploadValidation(data);
                } else {
                    this.uploaded.emit(this.uploadedFiles as PhotoData[]);
                }
                this.selectedFilesList = [];
            },
            (error: HttpErrorResponse) => {
                let errorText = 'Something went wrong. Please refresh the page and try again';
                if (this.isVisitorBulkUpload && error.status === 409) {
                    errorText = error.error.modelState.validationError.system[0];
                }
                this.toastrSevice.error(errorText, null);
                this.selectedFilesList = [];
                this.uploadInProgress = false;
            }
        );
    }

    handleUploadValidation(data: IParserResponse<any>): void {
        if (data.nonSuccessfullyParsedCount) {
            this.toastrSevice.error(`${data.nonSuccessfullyParsedCount} visitors were not uploaded`, null);
        }
        if (data.successfullyParsedCount) {
            this.toastrSevice.success(`${data.successfullyParsedCount} visitors successfully uploaded`, null);
            this.bulkVisitorFileUploaded.emit(data as IParserResponse<unknown>);
        }

    }

    handleCleUploadValidation(data: IParserResponse<any>): void {
        if (data.nonSuccessfullyParsedCount) {
            this.toastrSevice.error(`${data.nonSuccessfullyParsedCount} visitors were not uploaded`, null);
        }
        if (data.successfullyParsedCount) {
            this.toastrSevice.success(`${data.successfullyParsedCount} visitors successfully uploaded`, null);
            this.uploaded.emit(this.uploadedFiles as PhotoData[]);
        }

    }

    uploadVisitorFile(file: File): Observable<unknown> {
        const visitorsFile = new FormData();
        visitorsFile.append('formFile', file);
        visitorsFile.append('fileName', file.name);
        return this.visitorUploadService.uploadVisitorFile(this.requestCategoryId, visitorsFile,this.isExtension);
    }

    
    uploadCleFile(file: File): Observable<unknown> {
        const cleFile = new FormData();
        cleFile.append('formFile', file);
        cleFile.append('fileName', file.name);
        return this.cleUploadService.uploadFile(this.isExtension, cleFile);
    }

    deleteFile(index: number): void {
        this.uploadedFiles.splice(index, 1);
        this.uploaded.emit(this.uploadedFiles as PhotoData[]);
    }

    // Loading file to get image dimensions
    checkPhotoDimensions(filesList: File[]): Observable<{width: number, height: number}> {
        return new Observable<{width: number, height: number}>
        (observer => {
            const img = new Image();
            img.src = window.URL.createObjectURL(filesList[0]);
            img.onload = () => {
                const height = img.height;
                const width = img.width;
                observer.next({ width, height });
                URL.revokeObjectURL(img.src);
                observer.complete();
            };
            // Not an image
            img.onerror = () => {
                observer.complete();
            };
        });
    }
}
